import { ampli } from 'lib-shared-tracking';
import { AsyncStatus } from 'lib-shared-types-api';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { useCallback, useEffect, useState } from 'react';
import { useMap } from 'react-use';

import { CommandLauncherPerformActionFn } from '..';
import {
  CommandLauncherAction,
  CommandLauncherHint,
  CommandLauncherRegisterActionsFn,
  CommandLauncherScope,
} from '../commandLauncher.types';
import { CommandLauncherContext } from '../commandLauncherContext';

/** Converts CommandLauncherScope to a string for tracking. */
const getScopeName = (scope: CommandLauncherScope): string => {
  switch (scope) {
    case CommandLauncherScope.User:
      return 'user';
    case CommandLauncherScope.Conversation:
      return 'conversation';
    case CommandLauncherScope.Macro:
      return 'macro';
    default:
      return 'main';
  }
};

type CommandLauncherProviderProps = PropsWithChildren & {
  /** The initial command launcher actions. */
  actions?: readonly CommandLauncherAction[];
};

export const CommandLauncherProvider = ({
  actions: initialActions,
  children,
}: CommandLauncherProviderProps): JSX.Element => {
  const [, performedActionsActions] = useMap<
    Record<CommandLauncherAction['id'], string>
  >({});
  const [asyncStatus, setAsyncStatus] = useState<AsyncStatus>('fulfilled');
  const [actions, setActions] = useState<readonly CommandLauncherAction[]>(
    initialActions ?? [],
  );

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [typeAhead, setTypeAhead] = useState('');
  const [hints, setHints] = useState<readonly CommandLauncherHint[]>();
  const [isInputReadOnly, setIsInputReadOnly] = useState(false);
  const [inputSuffixElement, setInputSuffixElement] =
    useState<HTMLElement | null>(null);

  const scope =
    Object.values(CommandLauncherScope).find((scope) => scope === query[0]) ??
    CommandLauncherScope.Main;

  const scopeQuery =
    scope !== CommandLauncherScope.Main ? query.split(scope)[1] : query;

  // Track opened event
  useEffect(() => {
    if (isOpen) {
      ampli.commandPaletteOpened({ openedBy: 'keyboard' });
    }
  }, [isOpen]);

  const registerActions = useCallback<CommandLauncherRegisterActionsFn>(
    (actions) => {
      setActions((currentActions) => currentActions.concat(actions));

      return () => {
        const actionIds = actions.map(({ id }) => id);
        setActions((currentActions) =>
          currentActions.filter(({ id }) => !actionIds.includes(id)),
        );
      };
    },
    [],
  );

  const performAction = useCallback<CommandLauncherPerformActionFn>(
    (action, { skipTracking = false } = {}) => {
      if (!skipTracking) {
        // Track action selected
        ampli.commandPaletteActionSelected({
          actionLabel: action.label,
          actionSection: action.section,
          commandName: action.commandName,
          query: query || undefined,
          scope: getScopeName(scope),
        });

        // Track closed by action selected (unless keepOpen is true)
        if (!action.keepOpen) {
          ampli.commandPaletteClosed({
            closedBy: 'actionSelected',
            query: query || undefined,
            scope: getScopeName(scope),
          });
        }
      }

      action.performFn?.();

      performedActionsActions.set(action.id, new Date().toISOString());

      // Clear the input after action is performed (unless action wants to keep modal open)
      if (!action.keepOpen) {
        setQuery('');
      }
    },
    [performedActionsActions, query, scope, setQuery],
  );

  return (
    <CommandLauncherContext.Provider
      value={{
        actions,
        asyncStatus,
        hints,
        inputSuffixElement,
        isActive,
        isInputReadOnly,
        isOpen,
        query,
        scope,
        scopeQuery,
        typeAhead,
        performAction,
        registerActions,
        setAsyncStatus,
        setHints,
        setInputSuffixElement,
        setIsActive,
        setIsInputReadOnly,
        setIsOpen,
        setQuery,
        setTypeAhead,
      }}
    >
      {children}
    </CommandLauncherContext.Provider>
  );
};
